
import { AddressType } from '@uplift/types';
import { FormikHelpers } from 'formik';
import { useAddressVerificationContext } from './address-verification-context';

export type SubmitResult = {
  success: boolean;
};

type GetAddressFromFormikValues<T> = {
  getAddressFromFormikValues: (formikValues: T) => AddressType;
};

export type HandleSubmitType<T> = {
  fn: (values: T, formikHelpers: FormikHelpers<T>) => Promise<SubmitResult | void> | void;
  config: {
    shouldCheck: boolean | ((address: AddressType, formikValues: T) => boolean);
    initialAddress: AddressType;
  } & (T extends AddressType ? {getAddressFromFormikValues?: never} : GetAddressFromFormikValues<T>);
};

export const useWithAddressVerify = () => {
  const { state: { parsedVerifiedAddress }, dispatch } = useAddressVerificationContext();

  const compareAddresses = (address: AddressType, parsedVerifiedAddress: AddressType) => {
    if (parsedVerifiedAddress === null) return true;
    const keys: (keyof AddressType)[] = [
      'address1',
      'city',
      'state',
      'zip' ];

    return !(keys.some(key => address[key]?.trim?.() !== parsedVerifiedAddress[key]));
  };

  const checkReverify = (address: AddressType, initialAddress: AddressType): boolean => {
    if (parsedVerifiedAddress !== null) {
      const verifiedMatches = compareAddresses(address, parsedVerifiedAddress);

      if (verifiedMatches) return false;
    }
    const initialMatches = compareAddresses(address, initialAddress);

    return !initialMatches;
  };

  const withAddressVerify =
      <T extends AddressType | object>(fn: HandleSubmitType<T>['fn'], config: HandleSubmitType<T>['config']) =>
        (values: T, formikHelpers: FormikHelpers<T>) => {

          // types are setup so that if values does not extend AddressType, then getAddressFromFormikValues is required
          // so, address will be defined
          let address: AddressType = {} as AddressType;

          if ('address1' in values) {
            address = {
              address1: values.address1,
              address2: values.address2,
              city: values.city,
              state: values.state,
              zip: values.zip
            };
          } else if (config.getAddressFromFormikValues) {
            address = config.getAddressFromFormikValues(values);
          }

          const shouldCheckReverify = typeof config.shouldCheck === 'function' ? config.shouldCheck(address, values) : config.shouldCheck;

          const shouldReverify = shouldCheckReverify && checkReverify(address, config.initialAddress);

          if (shouldReverify) {
            dispatch({
              type: 'setShouldReverifyAddress',
              payload: {
                shouldReverifyAddress: true
              }
            });
            formikHelpers.setSubmitting(false);
            return;
          } else {
            dispatch({
              type: 'setShouldReverifyAddress',
              payload: {
                shouldReverifyAddress: false
              }
            });
            return fn(values, formikHelpers);
          }
        };

  return withAddressVerify;
};
